import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError, apiFetch } from '../lib/api.js';
import { authClient } from '../lib/authClient.js';
import { noteSignedIn } from '../lib/clientErrors.js';

import type { Session, SessionContextValue, SessionStatus } from './types.js';

/**
 * Exported so tests can mount a component against a known session without
 * standing up a fake API. Application code should use `useSession()`.
 */
export const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<SessionStatus>('loading');

  const refresh = useCallback(async () => {
    try {
      const next = await apiFetch<Session>('/api/me');
      // C-32, set before the state change rather than in an effect: see below.
      noteSignedIn(true);
      setSession(next);
      setStatus('authenticated');
    } catch (error) {
      // A 401 is the normal "not signed in" answer, not a failure worth
      // surfacing. Anything else also leaves the user anonymous, but the
      // console keeps the detail for debugging.
      if (!(error instanceof ApiError && error.isUnauthenticated)) {
        console.error('Could not load the current session', error);
      }
      noteSignedIn(false);
      setSession(null);
      setStatus('anonymous');
    }
  }, []);

  const signOut = useCallback(async () => {
    await authClient.signOut();
    noteSignedIn(false);
    setSession(null);
    setStatus('anonymous');
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /*
   * C-32. A crash report says whether somebody was signed in, yes or no, and
   * nothing about who. The error boundary sits outside this provider on
   * purpose, so it is told directly, in `refresh` and `signOut` above.
   *
   * **Not in an effect - WAIT-PL question Q-1.** It was, and every crash report
   * said `false`. An effect runs after a render commits, and a render that
   * throws never commits: a page crashing in the render that first shows it to
   * a signed-in person discarded the very commit whose effect would have said
   * so. Setting it before the state change means it is already true by the
   * time anything renders with the session.
   */

  const value = useMemo<SessionContextValue>(
    () => ({ status, session, refresh, signOut }),
    [status, session, refresh, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) {
    throw new Error('useSession must be used inside a <SessionProvider>.');
  }
  return value;
}
